import { z } from 'zod';

/**
 * Date helpers. Business dates are calendar dates (no time). The app's time zone is Europe/Bucharest
 * (spec §3); "today" is derived from it. ISO dates (YYYY-MM-DD) travel over the wire; the UI shows DD.MM.YYYY.
 */

export const APP_TIME_ZONE = 'Europe/Bucharest';
export const DATE_FORMAT_DEFAULT = 'DD.MM.YYYY';

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DMY_RE = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;

export type IsoDate = string; // YYYY-MM-DD

/** Today's calendar date in the app time zone as YYYY-MM-DD. */
export function todayIso(now: Date = new Date(), timeZone: string = APP_TIME_ZONE): IsoDate {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

export function isIsoDate(value: unknown): value is IsoDate {
  if (typeof value !== 'string') return false;
  const m = ISO_DATE_RE.exec(value);
  if (!m) return false;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return (
    d.getUTCFullYear() === Number(m[1]) &&
    d.getUTCMonth() === Number(m[2]) - 1 &&
    d.getUTCDate() === Number(m[3])
  );
}

/** Parse ISO (YYYY-MM-DD), DD.MM.YYYY or a Date into YYYY-MM-DD. Returns null for empty input; throws for garbage. */
export function toIsoDate(value: Date | string | null | undefined): IsoDate | null {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) throw new Error('Invalid date');
    return `${value.getUTCFullYear()}-${pad2(value.getUTCMonth() + 1)}-${pad2(value.getUTCDate())}`;
  }
  const s = value.trim();
  if (isIsoDate(s)) return s;
  const dmy = DMY_RE.exec(s);
  if (dmy) {
    const iso = `${dmy[3]}-${pad2(Number(dmy[2]))}-${pad2(Number(dmy[1]))}`;
    if (isIsoDate(iso)) return iso;
    throw new Error(`Invalid date: ${value}`);
  }
  // ISO datetime → take the date part in the app time zone
  const t = new Date(s);
  if (!Number.isNaN(t.getTime()) && /\d{4}-\d{2}-\d{2}T/.test(s)) return todayIso(t);
  throw new Error(`Invalid date: ${value}`);
}

/** YYYY-MM-DD → Date at UTC midnight (what Prisma expects for @db.Date). */
export function isoToDate(iso: IsoDate): Date {
  if (!isIsoDate(iso)) throw new Error(`Invalid ISO date: ${iso}`);
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

export function dateToIso(value: Date | string | null | undefined): IsoDate | null {
  return toIsoDate(value);
}

/** Format a date as DD.MM.YYYY (or another simple pattern with DD, MM, YYYY tokens). */
export function formatDate(
  value: Date | string | null | undefined,
  format: string = DATE_FORMAT_DEFAULT,
): string {
  const iso = toIsoDate(value);
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return format.replace('YYYY', y).replace('MM', m).replace('DD', d);
}

export function addDays(iso: IsoDate, days: number): IsoDate {
  const d = isoToDate(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return toIsoDate(d)!;
}

export function addMonths(iso: IsoDate, months: number): IsoDate {
  const d = isoToDate(iso);
  const day = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + months);
  const lastDay = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(day, lastDay));
  return toIsoDate(d)!;
}

/** Monday–Friday working days (no holiday calendar in v1). */
export function addWorkingDays(iso: IsoDate, workingDays: number): IsoDate {
  const d = isoToDate(iso);
  let remaining = Math.abs(workingDays);
  const step = workingDays >= 0 ? 1 : -1;
  while (remaining > 0) {
    d.setUTCDate(d.getUTCDate() + step);
    const dow = d.getUTCDay();
    if (dow !== 0 && dow !== 6) remaining--;
  }
  return toIsoDate(d)!;
}

export function isWeekend(iso: IsoDate): boolean {
  const dow = isoToDate(iso).getUTCDay();
  return dow === 0 || dow === 6;
}

/** b − a in whole days. */
export function diffDays(a: IsoDate, b: IsoDate): number {
  return Math.round((isoToDate(b).getTime() - isoToDate(a).getTime()) / 86_400_000);
}

export function compareIso(a: IsoDate, b: IsoDate): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

export function yearOf(iso: IsoDate): number {
  return Number(iso.slice(0, 4));
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** zod: accepts YYYY-MM-DD or DD.MM.YYYY (or a Date) and yields YYYY-MM-DD. */
export const isoDateSchema = z.union([z.string(), z.date()]).transform((v, ctx) => {
  try {
    const iso = toIsoDate(v);
    if (!iso) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Date is required' });
      return z.NEVER;
    }
    return iso;
  } catch {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Invalid date "${String(v)}" (use YYYY-MM-DD)` });
    return z.NEVER;
  }
});

/** zod: optional/nullable date; empty string and null clear the value. */
export const isoDateNullableSchema = z.union([z.string(), z.date(), z.null()]).transform((v, ctx) => {
  if (v === null || v === '') return null;
  try {
    return toIsoDate(v);
  } catch {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Invalid date "${String(v)}" (use YYYY-MM-DD)` });
    return z.NEVER;
  }
});

/** "22 September 2026" / "22. September 2026" for documents. */
export function formatLongDate(iso: IsoDate, lang: 'EN' | 'DE' | 'en' | 'de' = 'EN'): string {
  const d = isoToDate(iso);
  const opts: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  };
  return d.toLocaleDateString(String(lang).toUpperCase() === 'DE' ? 'de-DE' : 'en-GB', opts);
}
